import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Fab,
  Tooltip,
} from '@mui/material';
import {
  PeopleAlt as TotalIcon,
  CheckCircle as AccountedIcon,
  PersonOff as NotAccountedIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { appColors } from '../Config/Theme';
import { IncidentRosterPerson, PersonStatus } from '../Types/Incident';
import { getIncidentRoster, getIncidentSummary, getIncidentDetail } from '../Services/IncidentService';
import IncidentMetricStrip from '../Components/Shared/IncidentMetricStrip';
import CurrentRosterViewBar from '../Components/Shared/CurrentRosterViewBar';
import RosterPersonRow from '../Components/Headcount/RosterPersonRow';
import HeadcountBottomBar from '../Components/Headcount/HeadcountBottomBar';
import HeadcountToolbar from '../Components/Headcount/HeadcountToolbar';
import HeadcountFilterDrawer from '../Components/Headcount/HeadcountFilterDrawer';
import HeadcountActiveFilters from '../Components/Headcount/HeadcountActiveFilters';
import HeadcountSaveBeforeLeaveDialog from '../Components/Headcount/HeadcountSaveBeforeLeaveDialog';
import NeedsAttentionBanner from '../Components/Headcount/NeedsAttentionBanner';
import HeadcountEmptyState from '../Components/Headcount/HeadcountEmptyState';
import { AlphaRange } from '../Utils/alphaRangeHelpers';
import { personMatchesAlphaRange, getLastName } from '../Utils/alphaRangeHelpers';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import AddUnlistedPersonDialog from '../Components/Headcount/AddUnlistedPersonDialog';
import IncidentInfoBar from '../Components/Incidents/IncidentInfoBar';
import ClosedIncidentBanner from '../Components/Incidents/ClosedIncidentBanner';
import { isIncidentClosed, canEditClosedRoster } from '../Utils/incidentHelpers';
import { getUiPersonStatus } from '../Utils/headcountStatus';
import { getPersonTypeLabel } from '../Utils/personTypeLabels';
import { personMatchesPersonType, personMatchesSourceFilter } from '../Utils/personHelpers';
import type { SourceFilter } from '../Utils/personHelpers';
import { useSyncStatus } from '../Hooks/useSyncStatus';
import { useHeadcountPendingSync } from '../Hooks/useHeadcountPendingSync';
import { useHeadcountNavigationGuard } from '../Hooks/useHeadcountNavigationGuard';
import { useSecurity } from '../Contexts/SecurityProvider';
import { useIncidentAssignments } from '../Hooks/useIncidentAssignments';
import SyncStatusIndicator from '../Components/Elements/SyncStatusIndicator';
import {
  SortMode,
  personMatchesSearch,
  getActiveAdvancedFilterCount,
} from '../Utils/headcountFilters';
import { buildTenantCompanyOptions, applyTenantCompanyFilter } from '../Utils/tenantCompanyHelpers';
import { PersonType } from '../Types/enums';
import { useIncidentRealtime } from '../Hooks/useIncidentRealtime';
import { applyRosterPersonStatusChanged } from '../realtime/incidentRealtimeHelpers';
import type {
  RosterPersonStatusChangedEvent,
  IncidentClosedEvent,
  IncidentRefreshRequestedEvent,
  RealtimeConnectionState,
} from '../realtime/incidentRealtimeTypes';
import {
  type RosterScope,
  isPersonDefaultVisible,
  isAssignedOfficeNoBadge,
  computeRosterScopeCounts,
  filterRosterByScope,
  deriveScopeCountsFromSummary,
  ROSTER_SCOPE_LABELS,
} from '../features/incidents/accountabilityScope';
import type { RosterScopeCountsDto } from '../Types/Incident';
import { useLinkedAccounting } from '../Hooks/useLinkedAccounting';
import LinkedAccountingDialog from '../Components/Headcount/LinkedAccountingDialog';

const ROSTER_REFRESH_INTERVAL = 5_000;
const ROSTER_REFRESH_INTERVAL_REALTIME = 60_000; // Slow fallback when realtime is active

const HeadcounterMainPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Data state ──
  const [roster, setRoster] = useState<IncidentRosterPerson[]>([]);
  const [incidentName, setIncidentName] = useState('');
  const [startedAt, setStartedAt] = useState('');
  const [incidentLocation, setIncidentLocation] = useState('');
  const [incidentType, setIncidentType] = useState<string | undefined>(undefined);
  const [incidentStatus, setIncidentStatus] = useState<string | undefined>(undefined);
  const [totalRosterCount, setTotalRosterCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [summaryScopeCounts, setSummaryScopeCounts] = useState<RosterScopeCountsDto | null>(null);

  // ── Online/sync state ──
  const syncStatus = useSyncStatus();

  // ── Permissions ──
  const { hasPermission, hasAnyRole, security } = useSecurity();
  const canAddPerson = hasPermission('Roster.AddUnlistedPerson');

  // ── Filters ──
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<PersonStatus | 'all'>(
    (searchParams.get('status') as PersonStatus | 'all') || 'all',
  );
  const [alphaRange, setAlphaRange] = useState<AlphaRange>((searchParams.get('alpha') as AlphaRange) || 'All');
  const [sortBy, setSortBy] = useState<SortMode>('lastNameAsc');
  const [personTypeFilter, setPersonTypeFilter] = useState<string[]>([]);
  const [rosterContextFilters, setRosterContextFilters] = useState<SourceFilter[]>([]);
  const [tenantCompanyFilter, setTenantCompanyFilter] = useState<string[]>([]);
  const [viewId, setViewId] = useState<string | undefined>(searchParams.get('viewId') || undefined);
  const [viewLabel] = useState<string | undefined>(undefined);

  // ── Scope selector ──
  const [rosterScope, setRosterScope] = useState<RosterScope>('accountability');

  // Derive a friendly label from viewId (e.g. "practice:Information Technology" → "Practice: Information Technology")
  const viewFilterLabel = useMemo(() => {
    if (!viewId) return undefined;
    const colonIdx = viewId.indexOf(':');
    if (colonIdx < 0) return viewId;
    const viewType = viewId.substring(0, colonIdx);
    const viewValue = viewId.substring(colonIdx + 1);
    const TYPE_LABELS: Record<string, string> = {
      practice: 'Practice',
      floor: 'Floor',
      persontype: 'Person Type',
      rostercontext: 'Roster Context',
      location: 'Location',
      company: 'Company',
    };
    const typeLabel = TYPE_LABELS[viewType.toLowerCase()] ?? viewType;
    return `${typeLabel}: ${viewValue}`;
  }, [viewId]);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addPersonSuccess, setAddPersonSuccess] = useState(false);
  const [addedPersonName, setAddedPersonName] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [realtimeSnackbar, setRealtimeSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // ── Load roster ──
  const loadRoster = useCallback(async (showLoading = true) => {
    if (!incidentId) return;
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const [rosterResult, summaryResult, detailResult] = await Promise.all([
        getIncidentRoster(incidentId, { viewId, scope: 'full' }),
        getIncidentSummary(incidentId).catch(() => null),
        getIncidentDetail(incidentId).catch(() => null),
      ]);
      setRoster(rosterResult.people);
      setTotalRosterCount(rosterResult.people.length);
      if (rosterResult.serverTimeUtc) setLastUpdated(rosterResult.serverTimeUtc);
      const sum = summaryResult?.summary;
      setIncidentName(sum?.incidentName || detailResult?.incidentName || '');
      setStartedAt(sum?.startedAtUtc || detailResult?.startedAtUtc || '');
      setIncidentLocation(sum?.location || detailResult?.location || '');
      setIncidentType(sum?.incidentType ?? detailResult?.incidentType);
      setIncidentStatus(sum?.status ?? detailResult?.status);
      if (sum) {
        setSummaryScopeCounts(deriveScopeCountsFromSummary(sum));
      }
      if (summaryResult?.serverTimeUtc) setLastUpdated(summaryResult.serverTimeUtc);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roster.');
    } finally {
      setLoading(false);
    }
  }, [incidentId, viewId]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  // ── Incident closed check ──
  const isClosed = isIncidentClosed({ status: incidentStatus });

  // ── Closed-incident roster edit permission ──
  const { assignments } = useIncidentAssignments(isClosed ? incidentId : undefined);
  const closedRosterEditable = isClosed && canEditClosedRoster({
    hasAnyRole,
    currentUserId: security?.appUserId,
    assignments,
  });
  const actionsDisabled = (isClosed && !closedRosterEditable) || !syncStatus.isOnline;

  // ── Pending sync hook ──
  const pendingSync = useHeadcountPendingSync({
    incidentId,
    onSyncStart: () => syncStatus.markSaving(),
    onSyncSuccess: (serverTimeUtc) => {
      if (serverTimeUtc) setLastUpdated(serverTimeUtc);
      syncStatus.markSynced(serverTimeUtc);
    },
    onSyncError: (msg) => syncStatus.markError(msg),
    onRosterUpdate: setRoster,
    onRefreshRoster: () => loadRoster(false),
  });

  const {
    pendingUpdates,
    pendingRef,
    hasPendingChanges,
    isSyncing,
    syncError,
    markStatusPending,
    flushPendingStatusUpdates,
    scheduleDelayedSync,
    cancelDelayedSync,
    discardPendingStatusUpdates,
    clearSyncError,
    setPendingUpdates,
  } = pendingSync;

  // ── Navigation guard ──
  const navGuard = useHeadcountNavigationGuard({
    hasPendingChanges,
    isSyncing,
    isOnline: syncStatus.isOnline,
    flushPendingStatusUpdates,
    cancelDelayedSync,
    discardPendingStatusUpdates,
  });

  // ── Realtime SignalR ──
  const handleRealtimeStatusChanged = useCallback((event: RosterPersonStatusChangedEvent) => {
    // Check correlationId for optimistic reconciliation
    const pending = pendingRef.current;
    const matchesPending = event.correlationId && Array.from(pending.values()).some(
      (p) => p.clientUpdateId === event.correlationId,
    );
    if (matchesPending && event.correlationId) {
      // Clear the matching pending update — server confirmed
      const cid = event.correlationId;
      setPendingUpdates((prev) => {
        const next = new Map(prev);
        const entries = Array.from(next.entries());
        for (let i = 0; i < entries.length; i++) {
          if (entries[i][1].clientUpdateId === cid) {
            next.delete(entries[i][0]);
            break;
          }
        }
        return next;
      });
    }

    // Apply the authoritative status from the event
    setRoster((prev) => applyRosterPersonStatusChanged(prev, event));

    // Update counts from event (authoritative, no local increment)
    if (event.counts) {
      setTotalRosterCount(event.counts.total);
    }

    syncStatus.markSynced(event.lastUpdatedAtUtc);
  }, [syncStatus, pendingRef, setPendingUpdates]);

  const handleRealtimeIncidentClosed = useCallback((event: IncidentClosedEvent) => {
    setIncidentStatus('closed');
    setRealtimeSnackbar({
      open: true,
      message: event.closedByDisplayName
        ? `Incident closed by ${event.closedByDisplayName}`
        : 'Incident has been closed',
    });
  }, []);

  const handleRealtimeRefreshRequested = useCallback((event: IncidentRefreshRequestedEvent) => {
    loadRoster(false);
    const msg = event.reason === 'manualAdd'
      ? 'New person added. Roster refreshed.'
      : 'Roster refreshed.';
    setRealtimeSnackbar({ open: true, message: msg });
  }, [loadRoster]);

  const handleRealtimeReconnect = useCallback(() => {
    loadRoster(false);
    syncStatus.markSynced();
  }, [loadRoster, syncStatus]);

  const handleRealtimeConnectionStateChanged = useCallback((state: RealtimeConnectionState) => {
    if (state === 'reconnecting') {
      syncStatus.markReconnecting();
    } else if (state === 'connected') {
      syncStatus.markSynced();
    }
  }, [syncStatus]);

  const { connectionState: realtimeState } = useIncidentRealtime({
    incidentId,
    enabled: !loading && !isClosed,
    onRosterPersonStatusChanged: handleRealtimeStatusChanged,
    onIncidentClosed: handleRealtimeIncidentClosed,
    onIncidentRefreshRequested: handleRealtimeRefreshRequested,
    onReconnect: handleRealtimeReconnect,
    onConnectionStateChanged: handleRealtimeConnectionStateChanged,
  });

  const realtimeConnected = realtimeState === 'connected';

  // ── Effective status (local pending overrides server) ──
  const getEffectiveStatus = useCallback(
    (person: IncidentRosterPerson): PersonStatus => {
      const pending = pendingUpdates.get(person.personId);
      return pending ? pending.status : getUiPersonStatus(person);
    },
    [pendingUpdates],
  );

  // ── Scope counts: prefer summary-level API counts, fall back to client-computed ──
  const clientScopeCounts = useMemo(() => computeRosterScopeCounts(roster), [roster]);
  const scopeCounts = summaryScopeCounts ?? clientScopeCounts;

  // ── Scope-filtered roster (before view/alpha/search filtering) ──
  const scopeFilteredRoster = useMemo(() => {
    // When status filter is notAccounted, always show all notAccounted (auto-switch to full scope for that status)
    if (statusFilter === 'notAccounted') return roster;
    return filterRosterByScope(roster, rosterScope);
  }, [roster, rosterScope, statusFilter]);

  // ── Summary-base people: scoped to alpha (or search results) ──
  // Always use the accountability-scoped roster for KPI counts, even when
  // statusFilter widens scopeFilteredRoster to the full roster.
  const summaryBase = useMemo(() => {
    let pool = filterRosterByScope(roster, rosterScope);
    // Apply viewId filter first
    if (viewId) {
      const colonIdx = viewId.indexOf(':');
      if (colonIdx > 0) {
        const viewType = viewId.substring(0, colonIdx).toLowerCase();
        const viewValue = viewId.substring(colonIdx + 1).toLowerCase();
        pool = pool.filter((p) => {
          if (viewType === 'floor') return p.floor?.toLowerCase() === viewValue;
          if (viewType === 'practice') return p.practice?.toLowerCase() === viewValue;
          if (viewType === 'persontype') {
            if (viewValue === 'visitor') {
              const vkw = ['visitor', 'guest', 'contractor'];
              return p.personType ? vkw.some((kw) => p.personType!.toLowerCase().includes(kw)) : false;
            }
            return p.personType?.toLowerCase() === viewValue;
          }
          return true;
        });
      }
    }
    // If search is active, apply search across full pool (ignoring alpha)
    if (searchQuery.trim()) {
      pool = pool.filter((p) => personMatchesSearch(p, searchQuery.trim()));
    } else if (alphaRange !== 'All') {
      pool = pool.filter((p) => personMatchesAlphaRange(p, alphaRange));
    }
    const result = { total: pool.length, accounted: 0, notAccounted: 0, notChecked: 0 };
    pool.forEach((p) => {
      const s = getEffectiveStatus(p);
      if (s === 'accounted') result.accounted++;
      else if (s === 'notAccounted') result.notAccounted++;
      else result.notChecked++;
    });
    return result;
  }, [roster, rosterScope, viewId, searchQuery, alphaRange, getEffectiveStatus]);

  // Scope label for summary cards
  const scopeLabel = searchQuery.trim()
    ? 'of search results'
    : alphaRange !== 'All'
      ? `of last names ${alphaRange}`
      : rosterScope === 'accountability'
        ? 'of accountability roster'
        : rosterScope === 'assignedOfficeNoBadge'
          ? 'of assigned office / no badge'
          : 'of full roster';

  // Advanced filter count for the toolbar badge
  const advancedFilterCount = useMemo(() => getActiveAdvancedFilterCount({
    searchText: searchQuery,
    statusFilter,
    sortMode: sortBy,
    alphaFilter: alphaRange,
    personTypeFilters: personTypeFilter,
    rosterContextFilters,
    tenantCompanies: tenantCompanyFilter,
  }), [searchQuery, statusFilter, sortBy, alphaRange, personTypeFilter, rosterContextFilters, tenantCompanyFilter]);

  // Person type counts for the drawer
  const personTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    roster.forEach((p) => {
      if (p.isUnlisted) { counts[PersonType.Unlisted] = (counts[PersonType.Unlisted] ?? 0) + 1; return; }
      const pt = (p.personType ?? '').toLowerCase().trim();
      if (pt) counts[pt] = (counts[pt] ?? 0) + 1;
    });
    return counts;
  }, [roster]);

  // Roster context counts for the drawer
  const rosterContextCounts = useMemo(() => {
    const counts: Record<string, number> = { badgeData: 0, lobbyTrack: 0, assignedOffice: 0, assignedOfficeNoBadge: 0, manualAdd: 0 };
    roster.forEach((p) => {
      if (personMatchesSourceFilter(p, 'badgeData')) counts.badgeData++;
      if (personMatchesSourceFilter(p, 'lobbyTrack')) counts.lobbyTrack++;
      if (personMatchesSourceFilter(p, 'assignedOffice')) counts.assignedOffice++;
      if (personMatchesSourceFilter(p, 'assignedOfficeNoBadge')) counts.assignedOfficeNoBadge++;
      if (personMatchesSourceFilter(p, 'manualAdd')) counts.manualAdd++;
    });
    // Override assignedOfficeNoBadge with API-level scope count so the filter
    // drawer option is enabled even when the default view hides those people.
    counts.assignedOfficeNoBadge = scopeCounts.assignedOfficeNoBadgeTodayNotChecked
      ?? scopeCounts.assignedOfficeNoBadgeTodayCount
      ?? counts.assignedOfficeNoBadge;
    return counts;
  }, [roster, scopeCounts]);

  // Tenant company options and counts for the drawer
  const tenantCompanyOptions = useMemo(
    () => buildTenantCompanyOptions(roster, tenantCompanyFilter),
    [roster, tenantCompanyFilter],
  );
  const totalTenantCount = useMemo(
    () => roster.filter((p) => (p.personType ?? '').toLowerCase().trim() === PersonType.Tenant).length,
    [roster],
  );
  const tenantCompanyLabels = useMemo(() => {
    const map: Record<string, string> = {};
    tenantCompanyOptions.forEach((o) => { map[o.key] = o.label; });
    return map;
  }, [tenantCompanyOptions]);

  // ── Handle status change ──
  const linkedAccounting = useLinkedAccounting({
    incidentId,
    disabled: actionsDisabled,
    queueStatus: markStatusPending,
    getEffectiveStatus,
  });
  const handleStatusChange = linkedAccounting.changeStatus;

  // ── Auto-sync timer (only for active incidents or authorized closed-incident editors) ──
  useEffect(() => {
    if (isClosed && !closedRosterEditable) return;
    scheduleDelayedSync();
    return () => cancelDelayedSync();
  }, [isClosed, closedRosterEditable, scheduleDelayedSync, cancelDelayedSync]);

  // ── Periodic roster refresh to pick up other headcounters' updates ──
  // Use slower interval when realtime is connected (fallback reconciliation only)
  useEffect(() => {
    if (isClosed && !closedRosterEditable) return;
    const interval = realtimeConnected ? ROSTER_REFRESH_INTERVAL_REALTIME : ROSTER_REFRESH_INTERVAL;
    const refreshTimer = setInterval(() => {
      if (pendingRef.current.size === 0) {
        loadRoster(false);
      }
    }, interval);
    return () => clearInterval(refreshTimer);
  }, [loadRoster, isClosed, closedRosterEditable, realtimeConnected, pendingRef]);

  // ── Filtered + sorted roster ──
  const filteredRoster = useMemo(() => {
    let result = scopeFilteredRoster;

    // View filter (client-side) — viewId format is "type:value" e.g. "floor:Floor 3"
    if (viewId) {
      const colonIdx = viewId.indexOf(':');
      if (colonIdx > 0) {
        const viewType = viewId.substring(0, colonIdx).toLowerCase();
        const viewValue = viewId.substring(colonIdx + 1).toLowerCase();
        result = result.filter((p) => {
          if (viewType === 'floor') return p.floor?.toLowerCase() === viewValue;
          if (viewType === 'practice') return p.practice?.toLowerCase() === viewValue;
          if (viewType === 'persontype') {
            if (viewValue === 'visitor') {
              const vkw = ['visitor', 'guest', 'contractor'];
              return p.personType ? vkw.some((kw) => p.personType!.toLowerCase().includes(kw)) : false;
            }
            return p.personType?.toLowerCase() === viewValue;
          }
          return true;
        });
      }
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter((p) => getEffectiveStatus(p) === statusFilter);
    }

    // Person type filter (OR)
    if (personTypeFilter.length > 0) {
      result = result.filter((p) => personMatchesPersonType(p, personTypeFilter));
    }

    // Roster context filter (OR)
    if (rosterContextFilters.length > 0) {
      result = result.filter((p) =>
        rosterContextFilters.some((sf) => personMatchesSourceFilter(p, sf)),
      );
    }

    // Tenant company filter
    if (tenantCompanyFilter.length > 0) {
      result = applyTenantCompanyFilter(result, tenantCompanyFilter);
    }

    // Alpha range filter — only when search is NOT active
    if (alphaRange !== 'All' && !searchQuery.trim()) {
      result = result.filter((p) => personMatchesAlphaRange(p, alphaRange));
    }

    // Search filter
    if (searchQuery.trim()) {
      result = result.filter((p) => personMatchesSearch(p, searchQuery.trim()));
    }

    // Sort
    const lastNameCmp = (a: IncidentRosterPerson, b: IncidentRosterPerson) =>
      getLastName(a).localeCompare(getLastName(b)) || a.displayName.localeCompare(b.displayName);

    result = [...result].sort((a, b) => {
      if (sortBy === 'lastNameAsc') return lastNameCmp(a, b);
      if (sortBy === 'statusPriority') {
        const order: Record<PersonStatus, number> = { notAccounted: 0, notChecked: 1, accounted: 2 };
        const diff = order[getEffectiveStatus(a)] - order[getEffectiveStatus(b)];
        return diff !== 0 ? diff : lastNameCmp(a, b);
      }
      if (sortBy === 'floor') {
        const fa = a.floor ?? '';
        const fb = b.floor ?? '';
        const diff = fa.localeCompare(fb);
        return diff !== 0 ? diff : lastNameCmp(a, b);
      }
      if (sortBy === 'company') {
        const ca = a.companyName ?? '';
        const cb = b.companyName ?? '';
        const diff = ca.localeCompare(cb);
        return diff !== 0 ? diff : lastNameCmp(a, b);
      }
      // recentlyUpdated
      const aTime = a.lastUpdatedAtUtc ?? '';
      const bTime = b.lastUpdatedAtUtc ?? '';
      return bTime.localeCompare(aTime);
    });

    return result;
  }, [scopeFilteredRoster, viewId, statusFilter, personTypeFilter, rosterContextFilters, tenantCompanyFilter, alphaRange, searchQuery, sortBy, getEffectiveStatus]);

  // Clear advanced filters helper
  const clearAdvancedFilters = useCallback(() => {
    setAlphaRange('All');
    setPersonTypeFilter([]);
    setRosterContextFilters([]);
    setTenantCompanyFilter([]);
  }, []);

  // ── Roster-scope switching (clears stale group/view narrowing, stays on page) ──
  const switchRosterScope = useCallback((scope: RosterScope) => {
    setRosterScope(scope);
    setAlphaRange('All');
    setViewId(undefined);
    setStatusFilter('all');
    setRosterContextFilters([]);
  }, []);

  // ── Reset only the current narrowing (group/filters/search); keep roster scope ──
  const resetRosterView = useCallback(() => {
    setAlphaRange('All');
    setPersonTypeFilter([]);
    setRosterContextFilters([]);
    setTenantCompanyFilter([]);
    setSearchQuery('');
    setStatusFilter('all');
    setViewId(undefined);
  }, []);

  // ── CurrentRosterViewBar derived display (from the same state as the list) ──
  const activeGroupLabel = alphaRange !== 'All' && !searchQuery.trim()
    ? alphaRange.replace('-', '–')
    : null;

  const otherFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (searchQuery.trim()) parts.push(`Search: "${searchQuery.trim()}"`);
    if (viewFilterLabel) parts.push(viewFilterLabel);
    if (personTypeFilter.length > 0) {
      parts.push(`Person Type: ${personTypeFilter.map((p) => getPersonTypeLabel(p) ?? p).join(', ')}`);
    }
    if (tenantCompanyFilter.length > 0) {
      parts.push(`Tenant Company: ${tenantCompanyFilter.map((k) => tenantCompanyLabels[k] ?? k).join(', ')}`);
    }
    if (rosterContextFilters.length > 0) {
      parts.push(`Roster Context: ${rosterContextFilters.length}`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [searchQuery, viewFilterLabel, personTypeFilter, tenantCompanyFilter, tenantCompanyLabels, rosterContextFilters]);

  const isRosterNarrowed =
    alphaRange !== 'All'
    || !!searchQuery.trim()
    || !!viewId
    || personTypeFilter.length > 0
    || rosterContextFilters.length > 0
    || tenantCompanyFilter.length > 0;

  // ── Render ──
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2, color: appColors.text.body }}>Loading roster…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2, justifyContent: 'center' }}>{error}</Alert>
        <Button variant="contained" onClick={() => loadRoster()}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Breadcrumbs ── */}
      <AppBreadcrumbs items={[
        { label: 'Incidents', path: '/' },
        { label: incidentName || 'Incident Dashboard', path: `/incidents/${incidentId}/incidentdashboard` },
        { label: 'Headcount' },
      ]} />

      {/* ── Closed banner ── */}
      {isClosed && !closedRosterEditable && <ClosedIncidentBanner />}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading }}>
            Headcount
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.25 }}>
            {isClosed ? 'Final roster status' : 'Headcount in progress'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          {/* Sync status indicator — active incidents or authorized closed-incident editors */}
          {(!isClosed || closedRosterEditable) && (
            <SyncStatusIndicator
              state={syncStatus.state}
              lastSyncedAt={syncStatus.lastSyncedAt}
              lastUpdatedAt={lastUpdated}
              errorMessage={syncStatus.errorMessage}
              onRetry={() => flushPendingStatusUpdates('manual')}
            />
          )}
          {!isClosed && (
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(`/incidents/${incidentId}/review`)}
            >
              {pendingUpdates.size > 0 ? `Review Progress (${pendingUpdates.size} updates)` : 'Review Progress'}
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Incident Info ── */}
      {incidentName && (
        <IncidentInfoBar
          incidentName={incidentName}
          incidentType={incidentType ?? 0}
          location={incidentLocation}
          startedAtUtc={startedAt}
          status={incidentStatus}
        />
      )}

      {/* ── Current roster view bar ── */}
      <CurrentRosterViewBar
        scopeLabel={ROSTER_SCOPE_LABELS[rosterScope]}
        count={filteredRoster.length}
        countNoun="people"
        groupLabel={activeGroupLabel}
        filterSummary={otherFilterSummary}
        isNarrowed={isRosterNarrowed}
        onReset={resetRosterView}
      />

      {/* ── Summary cards (clickable status filters) ── */}
      <Box sx={{ py: 0.5, mb: 1 }}>
        <IncidentMetricStrip
          metrics={[
            {
              key: 'toAccount',
              icon: <TotalIcon />,
              label: 'People in Scope',
              value: summaryBase.total,
              description: 'Accountability roster',
              variant: 'toAccount',
              onClick: () => switchRosterScope('accountability'),
              selected: rosterScope === 'accountability',
              ariaLabel: `People in scope, ${summaryBase.total} in the accountability roster${rosterScope === 'accountability' ? ', selected' : ''}`,
            },
            {
              key: 'accounted',
              icon: <AccountedIcon />,
              label: 'Accounted For',
              value: summaryBase.accounted,
              description: summaryBase.total > 0 ? `${Math.round((summaryBase.accounted / summaryBase.total) * 100)}% of roster` : 'People accounted',
              variant: 'accounted',
              onClick: () => setStatusFilter(statusFilter === 'accounted' ? 'all' : 'accounted'),
              selected: statusFilter === 'accounted',
              ariaLabel: `Filter by accounted for, ${summaryBase.accounted} people accounted`,
            },
            {
              key: 'notAccounted',
              icon: <NotAccountedIcon />,
              label: 'Needs Attention',
              value: summaryBase.notAccounted,
              description: summaryBase.notAccounted > 0 ? 'Marked Not Accounted' : 'None need attention',
              variant: 'notAccounted',
              neutralWhenZero: true,
              onClick: () => setStatusFilter(statusFilter === 'notAccounted' ? 'all' : 'notAccounted'),
              selected: statusFilter === 'notAccounted',
              ariaLabel: `Filter by needs attention, ${summaryBase.notAccounted} people marked not accounted`,
            },
          ]}
          contextMetrics={[
            {
              key: 'fullRoster',
              label: 'Full Roster',
              value: scopeCounts.fullRosterCounts.total,
              onClick: () => switchRosterScope('full'),
              selected: rosterScope === 'full',
              ariaLabel: `Full Roster, ${scopeCounts.fullRosterCounts.total} people${rosterScope === 'full' ? ', selected' : ''}`,
            },
            {
              key: 'noBadgeToday',
              label: 'No Badge Today',
              value: scopeCounts.assignedOfficeNoBadgeTodayNotChecked ?? 0,
              onClick: () => switchRosterScope('assignedOfficeNoBadge'),
              selected: rosterScope === 'assignedOfficeNoBadge',
              ariaLabel: `No Badge Today, ${scopeCounts.assignedOfficeNoBadgeTodayNotChecked ?? 0} people${rosterScope === 'assignedOfficeNoBadge' ? ', selected' : ''}`,
            },
          ]}
        />
      </Box>

      {/* ── Sticky controls area ── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: appColors.surface.page || '#F5F6F7',
          pt: 1,
          pb: 1,
          mx: { xs: -2, sm: -3 },
          px: { xs: 2, sm: 3 },
          borderBottom: 'none',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Row 1: Search + Sort + Filters */}
        <HeadcountToolbar
          searchText={searchQuery}
          onSearchChange={setSearchQuery}
          sortMode={sortBy}
          onSortChange={setSortBy}
          activeFilterCount={advancedFilterCount}
          onFiltersClick={() => setFilterDrawerOpen(true)}
          alphaRange={alphaRange}
          onAlphaRangeChange={setAlphaRange}
        />

        {/* Row 2: Active filters summary */}
        <HeadcountActiveFilters
          filteredCount={filteredRoster.length}
          sortMode={sortBy}
          statusFilter={statusFilter}
          alphaFilter={alphaRange}
          personTypeFilters={personTypeFilter}
          rosterContextFilters={rosterContextFilters}
          tenantCompanyFilters={tenantCompanyFilter}
          tenantCompanyLabels={tenantCompanyLabels}
          viewFilterLabel={viewFilterLabel}
          onRemoveAlpha={() => setAlphaRange('All')}
          onRemovePersonType={(v) => {
            setPersonTypeFilter((prev) => prev.filter((x) => x !== v));
            // If removing Tenants, also clear tenant company selections
            if (v === PersonType.Tenant) setTenantCompanyFilter([]);
          }}
          onRemoveRosterContext={(v) => setRosterContextFilters((prev) => prev.filter((x) => x !== v))}
          onRemoveTenantCompany={(v) => setTenantCompanyFilter((prev) => prev.filter((x) => x !== v))}
          onClearAll={clearAdvancedFilters}
        />
      </Box>

      {/* ── Not Accounted banner ── */}
      {!isClosed && (
        <NeedsAttentionBanner
          notAccountedCount={summaryBase.notAccounted}
          onReviewClick={() => setStatusFilter('notAccounted')}
        />
      )}

      {/* ── Roster list ── */}
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: appColors.surface.card, borderRadius: '12px 12px 0 0', border: `1px solid ${appColors.border.default}`, pb: { xs: 10, sm: 12 } }}>
        {filteredRoster.length === 0 && totalRosterCount > 0 ? (
          rosterScope === 'accountability' && (scopeCounts.assignedOfficeNoBadgeTodayCount ?? 0) > 0 && scopeCounts.accountabilityCounts.total === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: appColors.text.body, mb: 1 }}>
                No people are currently in the default accountability roster.
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mb: 1.5 }}>
                Assigned-office employees without badge activity today are available under Assigned Office / No Badge.
              </Typography>
              <Button size="small" variant="outlined" onClick={() => setRosterScope('assignedOfficeNoBadge')} sx={{ fontWeight: 600, textTransform: 'none' }}>
                Show Assigned Office / No Badge
              </Button>
            </Box>
          ) : rosterScope === 'assignedOfficeNoBadge' ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: appColors.text.caption }}>
                No assigned-office / no-badge people for this incident.
              </Typography>
            </Box>
          ) : (
            <HeadcountEmptyState onClearFilters={() => { setSearchQuery(''); setStatusFilter('all'); setAlphaRange('All'); setPersonTypeFilter([]); setRosterContextFilters([]); setTenantCompanyFilter([]); }} />
          )
        ) : filteredRoster.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: appColors.text.caption }}>No roster data available.</Typography>
          </Box>
        ) : (
          filteredRoster.map((person, idx) => (
            <RosterPersonRow
              key={person.personId}
              person={person}
              effectiveStatus={getEffectiveStatus(person)}
              isPending={pendingUpdates.has(person.personId)}
              onStatusChange={actionsDisabled ? () => {} : handleStatusChange}
              avatarColorIndex={idx}
              readOnly={actionsDisabled}
            />
          ))
        )}
      </Box>

      {/* ── Bottom bar ── */}
      <Box>
        <HeadcountBottomBar
          showingStart={filteredRoster.length > 0 ? 1 : 0}
          showingEnd={filteredRoster.length}
          showingTotal={filteredRoster.length}
          viewLabel={viewLabel}
          viewingCount={filteredRoster.length}
          totalRosterCount={totalRosterCount}
          onSwitchToFullRoster={() => { setViewId(undefined); setStatusFilter('all'); setSearchQuery(''); clearAdvancedFilters(); }}
          autoSyncSeconds={5}
        />
      </Box>

      {/* ── Add Person FAB — active incidents or authorized closed-incident editors ── */}
      {(!isClosed || closedRosterEditable) && canAddPerson && (
        <Tooltip title={syncStatus.isOnline ? 'Add unlisted person' : 'Offline — cannot add person'} arrow>
          <span>
          <Fab
            color="primary"
            onClick={() => setAddPersonOpen(true)}
            disabled={!syncStatus.isOnline}
            aria-label="Add person"
            sx={{
              position: 'fixed',
              bottom: { xs: 16, sm: 16 },
              right: { xs: 16, sm: 'max(16px, calc((100vw - var(--sidebar-w, 0px) - 1200px) / 2 - 16px))' },
              bgcolor: appColors.primary.main,
              '&:hover': {
                bgcolor: appColors.primary.dark,
                boxShadow: `0 0 0 5px ${appColors.status.accent}30, 0 8px 28px ${appColors.status.accent}38`,
              },
              '&:focus-visible': {
                outline: `3px solid ${appColors.status.accent}`,
                outlineOffset: 3,
              },
              width: { xs: 56, sm: 64 },
              height: { xs: 56, sm: 64 },
              flexDirection: 'column',
              border: `2px solid ${appColors.status.accent}8C`,
              boxShadow: `0 0 0 5px ${appColors.status.accent}2E, 0 8px 24px ${appColors.status.accent}47`,
            }}
          >
            <AddIcon sx={{ fontSize: 22 }} />
            <Typography variant="caption" sx={{ color: '#FFFFFF', fontSize: '0.6rem', lineHeight: 1, mt: 0.25 }}>
              Add{'\n'}Person
            </Typography>
          </Fab>
          </span>
        </Tooltip>
      )}

      {/* ── Sync error snackbar ── */}
      <Snackbar
        open={!!syncError}
        autoHideDuration={6000}
        onClose={clearSyncError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={clearSyncError} sx={{ width: '100%' }}>
          {syncError}
        </Alert>
      </Snackbar>

      {/* ── Realtime event snackbar ── */}
      <Snackbar
        open={realtimeSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setRealtimeSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setRealtimeSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {realtimeSnackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Add Person placeholder snackbar ── */}
      <AddUnlistedPersonDialog
        open={addPersonOpen}
        incidentId={incidentId!}
        onClose={() => setAddPersonOpen(false)}
        onPersonAdded={(person, addedName) => {
          setAddPersonOpen(false);
          if (person) {
            setRoster((prev) => [person, ...prev]);
            setTotalRosterCount((prev) => prev + 1);
          } else {
            loadRoster(false);
          }
          setAddedPersonName(addedName || 'Person');
          setAddPersonSuccess(true);
        }}
      />
      <Snackbar
        open={addPersonSuccess}
        autoHideDuration={3000}
        onClose={() => setAddPersonSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setAddPersonSuccess(false)} sx={{ width: '100%' }}>
          {addedPersonName} added to roster.
        </Alert>
      </Snackbar>

      {/* ── Filter drawer ── */}
      <HeadcountFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        alphaFilter={alphaRange}
        onAlphaFilterChange={setAlphaRange}
        personTypeFilters={personTypeFilter}
        onPersonTypeFiltersChange={(v) => {
          setPersonTypeFilter(v);
          // If Tenants was deselected, clear tenant company filters
          if (!v.includes(PersonType.Tenant)) setTenantCompanyFilter([]);
        }}
        rosterContextFilters={rosterContextFilters}
        onRosterContextFiltersChange={setRosterContextFilters}
        onClearAll={clearAdvancedFilters}
        personTypeCounts={personTypeCounts}
        rosterContextCounts={rosterContextCounts}
        filteredCount={filteredRoster.length}
        tenantCompanyFilters={tenantCompanyFilter}
        onTenantCompanyFiltersChange={(v) => {
          setTenantCompanyFilter(v);
          // Auto-select Tenants person type when a company is selected
          if (v.length > 0 && !personTypeFilter.includes(PersonType.Tenant)) {
            setPersonTypeFilter((prev) => [...prev, PersonType.Tenant]);
          }
        }}
        tenantCompanyOptions={tenantCompanyOptions}
        totalTenantCount={totalTenantCount}
        onAssignedOfficeNoBadgeScopeSwitch={() => {
          const nextScope = rosterScope === 'assignedOfficeNoBadge' ? 'accountability' : 'assignedOfficeNoBadge';
          setRosterScope(nextScope);
          setRosterContextFilters([]);
          setFilterDrawerOpen(false);
        }}
        isAssignedOfficeNoBadgeScopeActive={rosterScope === 'assignedOfficeNoBadge'}
      />

      {/* ── Save-before-leave dialog ── */}
      <HeadcountSaveBeforeLeaveDialog
        open={navGuard.showDialog}
        state={navGuard.dialogState}
        pendingCount={pendingUpdates.size}
        errorMessage={navGuard.dialogError}
        onRetry={navGuard.handleRetry}
        onStay={navGuard.handleStay}
        onDiscard={navGuard.handleDiscard}
      />
      <LinkedAccountingDialog
        request={linkedAccounting.request}
        disabled={actionsDisabled}
        onConfirm={linkedAccounting.confirm}
        onCancel={linkedAccounting.cancel}
        onRetry={linkedAccounting.retry}
      />
    </Box>
  );
};

export default HeadcounterMainPage;
